// Active acute-illness follow-up widget (ILLNESS_EPISODE_TRACKING_PLAN.md, D-IE-3/13).
// Self-hides when there are no ongoing episodic illnesses. Simpler than the
// symptom card: no severity stepper, no sparkline — just name, "Day N", a status
// badge, a due pill, and four one-tap actions (Still sick / Recovered today /
// Recovered on… / Feeling worse), plus a Snooze affordance.

import { useState } from "react";
import {
  recordIllnessFollowUp,
  snoozeIllnessFollowUp,
  ApiError,
} from "../../../lib/api.js";
import { VirusIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";
import { QuickIllnessLogModal } from "./QuickIllnessLogModal.js";

function todayStr(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

const ACTION_BTN =
  "rounded-full border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 " +
  "hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

export function ActiveIllnessWidget({
  patient,
  openIllnesses,
  onOpenIllnessDone,
  onReload,
  onError,
  onPrependEvent,
  onShowToast,
}: WidgetProps) {
  const [logOpen, setLogOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [datePickerKey, setDatePickerKey] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState(todayStr);

  if (openIllnesses.length === 0) return null;

  const tz = patient.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const n = openIllnesses.length;

  async function act(
    key: string,
    action: "same" | "recovered" | "worse",
    recoveredOn?: string,
  ) {
    setBusyKey(key);
    try {
      await recordIllnessFollowUp({
        patientId: patient.patientId,
        key,
        action,
        ...(recoveredOn ? { recoveredOn } : {}),
        timezone: tz,
      });
      onOpenIllnessDone(key);
      setDatePickerKey(null);
      onReload();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Couldn't record illness update");
    } finally {
      setBusyKey(null);
    }
  }

  async function snooze(key: string) {
    setBusyKey(key);
    try {
      await snoozeIllnessFollowUp({ patientId: patient.patientId, key });
      onOpenIllnessDone(key);
      onReload();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Couldn't snooze");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <VirusIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Active illness</h2>
        <button
          onClick={() => setLogOpen(true)}
          className="ml-auto rounded-full bg-(--accent)/10 px-3 py-1 text-xs font-medium text-(--accent) hover:bg-(--accent)/20"
        >
          + Log illness
        </button>
      </div>

      <ul className="space-y-3">
        {openIllnesses.slice(0, 3).map((ill) => {
          const displayName = ill.name.charAt(0).toUpperCase() + ill.name.slice(1);
          const busy = busyKey === ill.key;
          return (
            <li key={ill.key} className="rounded-lg border border-gray-100 dark:border-gray-800 p-2.5">
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayName}</span>
                <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-600 dark:text-gray-400">
                  Day {ill.dayCount}
                </span>
                <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] capitalize text-gray-600 dark:text-gray-400">
                  {ill.status}
                </span>
                {ill.dueForCheckIn && (
                  <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
                    due
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5">
                <button disabled={busy} onClick={() => void act(ill.key, "same")} className={ACTION_BTN}>
                  Still sick
                </button>
                <button disabled={busy} onClick={() => void act(ill.key, "recovered")} className={ACTION_BTN}>
                  Recovered today
                </button>
                <button
                  disabled={busy}
                  onClick={() => {
                    setDateValue(todayStr());
                    setDatePickerKey(datePickerKey === ill.key ? null : ill.key);
                  }}
                  className={ACTION_BTN}
                >
                  Recovered on…
                </button>
                <button disabled={busy} onClick={() => void act(ill.key, "worse")} className={ACTION_BTN}>
                  Feeling worse
                </button>
                <button
                  disabled={busy}
                  onClick={() => void snooze(ill.key)}
                  className="rounded-full px-2.5 py-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-300"
                >
                  Snooze
                </button>
              </div>

              {datePickerKey === ill.key && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="date"
                    value={dateValue}
                    max={todayStr()}
                    onChange={(e) => setDateValue(e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                  />
                  <button
                    disabled={busy || !dateValue}
                    onClick={() => void act(ill.key, "recovered", dateValue)}
                    className="rounded-lg bg-(--accent) px-3 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-60"
                  >
                    Save
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {n > 3 && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">+{n - 3} more</p>}

      {logOpen && (
        <QuickIllnessLogModal
          patientId={patient.patientId}
          onClose={() => setLogOpen(false)}
          onLogged={(ref, payload, label) => {
            onPrependEvent(ref, payload);
            onShowToast(ref, label);
            onReload();
          }}
        />
      )}
    </section>
  );
}
